import React, { useState, useEffect, useRef, useCallback, useImperativeHandle } from "react";
import $ from "jquery";
import { useDropzone } from "react-dropzone";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import CommentsList from "../Shared/CommentsList/CommentsList.js";
import * as Shared from "./Shared.js";
import InsertFromProductInfoPopup from "./InsertFromProductInfoPopup.js";
import { AddDependenciesModal, SeeDependenciesModal } from "./SectionDependencies.js";

import "trumbowyg/dist/ui/trumbowyg.min.css";
import "trumbowyg/dist/plugins/table/ui/trumbowyg.table.min.css";

const ENTITY_TYPE = "compliance-technical-documents";

/*
 * A section is PREVIEWED (read-only HTML) until "Edit" mounts the Trumbowyg editor.
 * Save writes a new minor version (PUT Content -> _SectionVersion) and returns to preview.
 */

const TB_BTNS = [
    ["viewHTML"],
    ["historyUndo", "historyRedo"],
    ["formatting"],
    ["fontfamily"],
    ["fontsize"],
    ["strong", "em", "underline", "del"],
    ["superscript", "subscript"],
    ["justifyLeft", "justifyCenter", "justifyRight", "justifyFull"],
    ["unorderedList", "orderedList"],
    ["indent", "outdent"],
    ["link"],
    ["insertImage"],
    ["table"],
    ["horizontalRule"],
    ["removeformat"],
    ["fullscreen"],
];

const loadTrumbowyg = async () => {
    window.$ = $;
    window.jQuery = $;
    await import("trumbowyg");
    await import("trumbowyg/dist/plugins/history/trumbowyg.history.min.js");
    await import("trumbowyg/dist/plugins/fontfamily/trumbowyg.fontfamily.min.js");
    await import("trumbowyg/dist/plugins/fontsize/trumbowyg.fontsize.min.js");
    await import("trumbowyg/dist/plugins/indent/trumbowyg.indent.min.js");
    await import("trumbowyg/dist/plugins/table/trumbowyg.table.min.js");
};


const SectionEditor = React.forwardRef(({
    techDocId,
    section,               // tree node { Id, Title, SectionIdentifier, ParentId, NeedsReview, Jurisdictions, ... }
    sectionVersion,        // current _SectionVersion { Major, Minor, State, Name } or null
    number,                // computed outline number, e.g. "1.2"
    canEdit,               // version is an editable draft AND current user is an Admin (authorship is not an override)
    jurisdictionOptions,   // [{ Id, Name }]
    users,                 // [{ Id, Name, ... }] for the info panel + comments
    currentVersion,        // the selected _Version -- Author for the info panel
    reviewers,             // [{ MemberId, Name }] roster for the info panel
    currentUserId,
    onSaved,               // (sectionVersion) -> refresh the tree
    onDirtyChange,         // (bool)
    onOpenHistory,         // () -> open this section's version history
    onClearNeedsReview,    // () -> dismiss the "linked section changed" flag
    allSections,           // every section in the tree, for the "link to" picker
    numbers,               // { [sectionId]: "1.2" } outline numbers, for labeling that picker
    sectionLinks,          // every TechnicalDocumentationSectionLink in the document
    onAddLink,             // (sourceIdentifier, targetIdentifier, note) -> Promise
    onRemoveLink,          // (linkId) -> Promise
}, ref) => {
    const [content, setContent] = useState(null);       // { Id, ContentText, Summary }
    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editorReady, setEditorReady] = useState(false);
    const [title, setTitle] = useState(section?.Title || "");
    const [jurisdictions, setJurisdictions] = useState(section?.Jurisdictions || []);
    const [savedJurisdictions, setSavedJurisdictions] = useState(section?.Jurisdictions || []);
    const [addingJurisdiction, setAddingJurisdiction] = useState(false);
    const [savingJurisdictions, setSavingJurisdictions] = useState(false);
    const [dependencyPopup, setDependencyPopup] = useState(null);   // "add" | "see" | null
    const [showComments, setShowComments] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [showProductInfo, setShowProductInfo] = useState(false);
    const [error, setError] = useState(null);
    const [kebabOpen, setKebabOpen] = useState(false);
    const [reverting, setReverting] = useState(false);

    const editorElRef = useRef(null);
    const originalHtmlRef = useRef("");
    const kebabRef = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (kebabRef.current && !kebabRef.current.contains(e.target)) setKebabOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    // ---- load section content, reset to preview on section change ----
    useEffect(() => {
        if (!section) return;
        setTitle(section.Title || "");
        setJurisdictions(section.Jurisdictions || []);
        setSavedJurisdictions(section.Jurisdictions || []);
        setIsEditing(false);
        setSavedAt(null);
        loadContent(section.Id);
        return () => destroyEditor();
    }, [section?.Id]);

    const loadContent = (sectionId) => {
        setIsLoading(true);
        setEditorReady(false);
        setError(null);
        fetch(`/umbraco/api/TechnicalDocumentation/Content/?techDocId=${techDocId}&sectionId=${sectionId}`)
            .then((r) => { if (!r.ok) throw new Error("load"); return r.json(); })
            .then((data) => {
                const c = data?.content || data;
                setContent(c);
                setFiles(data?.contentFiles || []);
                originalHtmlRef.current = c?.ContentText || "";
            })
            .catch(() => setError("Could not load this section."))
            .finally(() => setIsLoading(false));
    };

    // ---- Trumbowyg lifecycle -- only while editing ----
    useEffect(() => {
        if (!isEditing || isLoading || !content || !editorElRef.current) return;

        let cancelled = false;
        (async () => {
            await loadTrumbowyg();
            if (cancelled || !editorElRef.current) return;
            const $ed = $(editorElRef.current);
            if (!$ed.data("trumbowyg")) {
                $ed.trumbowyg({ btns: TB_BTNS, autogrow: true, removeformatPasted: true });
                $ed.on("tbwchange tbwblur", () => {
                    onDirtyChange && onDirtyChange($ed.trumbowyg("html") !== originalHtmlRef.current);
                });
            }
            $ed.trumbowyg("html", content.ContentText || "");
            $ed.parent().find(".trumbowyg-editor").off("paste.sectionImageUpload").on("paste.sectionImageUpload", handleImagePaste);
            setEditorReady(true);
        })();

        return () => { cancelled = true; };
    }, [isEditing, isLoading, content]);

    const destroyEditor = () => {
        try {
            const $ed = $(editorElRef.current);
            $ed.parent().find(".trumbowyg-editor").off("paste.sectionImageUpload");
            if ($ed.data && $ed.data("trumbowyg")) $ed.trumbowyg("destroy");
        } catch (e) { /* noop */ }
        setEditorReady(false);
    };

    // Trumbowyg's own insertImage button only takes a URL -- pasting a screenshot/copied image
    // silently did nothing without this. Mirrors FieldTypes.js's ItemDetailsField_Text (used by
    // country notes etc.): drop in a loading-spinner placeholder immediately, upload the image to the
    // same shared Images/Image store, then swap the placeholder's src for the real URL once it lands.
    const handleImagePaste = async (e) => {
        const $ed = $(editorElRef.current);
        const clipboardData = e.originalEvent.clipboardData;
        if (!clipboardData?.items) return;

        const items = [...clipboardData.items];
        const imageItems = items.filter((item) => item.type.startsWith("image/"));
        const htmlItem = items.find((item) => item.type === "text/html");

        let htmlContent = "";
        let embeddedImages = [];
        let doc = null;

        if (htmlItem) {
            htmlContent = await new Promise((resolve) => htmlItem.getAsString(resolve));
            doc = new DOMParser().parseFromString(htmlContent, "text/html");
            embeddedImages = [...doc.querySelectorAll("img")].filter((img) => img.src.startsWith("data:image"));
        }

        // No images involved -- let Trumbowyg's own paste handling run as normal.
        if (imageItems.length === 0 && embeddedImages.length === 0) return;

        e.preventDefault();

        const placeholders = [];
        const batchId = Date.now();

        embeddedImages.forEach((img, index) => {
            const placeholderId = `upload-${batchId}-${index}`;
            placeholders.push({ id: placeholderId, base64: img.src });
            img.src = "/assets/js/frontend/public/loading-spinner.gif";
            img.setAttribute("style", "width:auto; height:auto; display:block; margin:10px 0;");
            img.setAttribute("data-upload-id", placeholderId);
        });
        if (doc) htmlContent = doc.body.innerHTML;

        if (!htmlContent && imageItems.length > 0) {
            imageItems.forEach((item, index) => {
                const placeholderId = `upload-${batchId}-i${index}`;
                placeholders.push({ id: placeholderId, file: item.getAsFile() });
                htmlContent += `<img data-upload-id="${placeholderId}" src="/assets/js/frontend/public/loading-spinner.gif" style="width:auto; height:auto; display:block; margin:10px 0;" />`;
            });
        }

        $ed.trumbowyg("execCmd", { cmd: "insertHTML", param: htmlContent });
        const editorElement = $ed.parent().find(".trumbowyg-editor");

        try {
            await Promise.all(placeholders.map(async (p) => {
                let base64 = p.base64;
                if (!base64) {
                    base64 = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(p.file);
                    });
                }
                const response = await fetch("/umbraco/api/Images/Image", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ entityType: ENTITY_TYPE, imageBase64: base64 }),
                });
                if (!response.ok) throw new Error("Upload failed");
                const imageId = await response.text();
                const imageUrl = `/umbraco/api/Images/Image/?id=${imageId}&entityType=${ENTITY_TYPE}`;
                editorElement.find(`[data-upload-id="${p.id}"]`)
                    .attr("src", imageUrl)
                    .removeAttr("data-upload-id")
                    .css({ width: "auto", height: "auto", maxWidth: "100%" });
            }));
        } catch (err) {
            setError("Could not upload a pasted image.");
        } finally {
            onDirtyChange && onDirtyChange($ed.trumbowyg("html") !== originalHtmlRef.current);
        }
    };

    const getHtml = () => {
        try {
            const $ed = $(editorElRef.current);
            if ($ed.data && $ed.data("trumbowyg")) return $ed.trumbowyg("html");
        } catch (e) { /* noop */ }
        return content?.ContentText || "";
    };

    const insertHtmlAtCursor = (html) => {
        try { $(editorElRef.current).trumbowyg("execCmd", { cmd: "insertHTML", param: html }); } catch (e) { /* noop */ }
        onDirtyChange && onDirtyChange(true);
    };

    // ---- edit / save / cancel ----
    const startEdit = () => { setError(null); setIsEditing(true); };

    const save = () => {
        if (!content) return Promise.resolve();
        setIsSaving(true);
        setError(null);
        const html = getHtml();

        const putContent = fetch(`/umbraco/api/TechnicalDocumentation/Content/?techDocId=${techDocId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ SectionId: section.Id, ContentText: html, Summary: content.Summary || "" }),
        });
        const putSection = fetch(`/umbraco/api/TechnicalDocumentation/Sections/?techDocId=${techDocId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...section, Title: title, Jurisdictions: jurisdictions }),
        });

        return Promise.all([putContent, putSection])
            .then(async ([rc, rs]) => {
                if (!rc.ok || !rs.ok) throw new Error("save");
                const body = await rc.json().catch(() => null);
                originalHtmlRef.current = html;
                setContent((prev) => ({ ...prev, ContentText: html }));
                setSavedAt(new Date().toISOString());
                setSavedJurisdictions(jurisdictions);
                onDirtyChange && onDirtyChange(false);
                destroyEditor();
                setIsEditing(false);
                onSaved && onSaved(body?.sectionVersion || null);
            })
            .catch((e) => { setError("Could not save this section."); throw e; })
            .finally(() => setIsSaving(false));
    };

    // Lets the parent pane trigger a save from its own "unsaved changes" leave-guard, without the
    // editor's internal state (title/jurisdictions/editor html) needing to live outside this component.
    useImperativeHandle(ref, () => ({ save }));

    const cancel = () => {
        try { $(editorElRef.current).trumbowyg("html", originalHtmlRef.current); } catch (e) { /* noop */ }
        setTitle(section.Title || "");
        onDirtyChange && onDirtyChange(false);
        destroyEditor();
        setIsEditing(false);
    };

    // Discards this draft/rejected version and opens a fresh draft matching the latest approved
    // content. Kebab stays open (rather than closing immediately) so the spinner is visible in flight.
    const revertToApproved = () => {
        if (reverting) return;
        setReverting(true);
        setError(null);
        fetch(`/umbraco/api/TechnicalDocumentation/RevertSectionVersion/?techDocId=${techDocId}&sectionId=${section.Id}`, { method: "POST" })
            .then((r) => { if (!r.ok) throw new Error("revert"); return r.json(); })
            .then((sv) => {
                setKebabOpen(false);
                loadContent(section.Id);
                onSaved && onSaved(sv);
            })
            .catch(() => setError("Could not revert this section."))
            .finally(() => setReverting(false));
    };

    // ---- jurisdictions (editable outside edit mode, saved on their own) ----
    const addJurisdiction = (id) => {
        const j = jurisdictionOptions.find((x) => x.Id == id);
        if (j && !jurisdictions.find((x) => x.Id == j.Id)) setJurisdictions([...jurisdictions, j]);
        setAddingJurisdiction(false);
    };
    const removeJurisdiction = (id) => setJurisdictions(jurisdictions.filter((x) => x.Id != id));

    const jurisdictionIds = (list) => list.map((j) => Number(j.Id)).sort((a, b) => a - b).join(",");
    const jurisdictionsChanged = jurisdictionIds(jurisdictions) !== jurisdictionIds(savedJurisdictions);

    const saveJurisdictions = () => {
        setSavingJurisdictions(true);
        setError(null);
        fetch(`/umbraco/api/TechnicalDocumentation/SectionJurisdictions/?techDocId=${techDocId}&sectionId=${section.Id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(jurisdictions),
        })
            .then((r) => { if (!r.ok) throw new Error("jurisdictions"); setSavedJurisdictions(jurisdictions); onSaved && onSaved(null); })
            .catch(() => setError("Could not save the jurisdictions."))
            .finally(() => setSavingJurisdictions(false));
    };

    // ---- per-section files ----
    const onDrop = useCallback((accepted) => {
        if (!section || !accepted.length) return;
        setIsUploading(true);
        const form = new FormData();
        accepted.forEach((f) => form.append("files", f));
        fetch(`/umbraco/api/TechnicalDocumentation/Files/?techDocId=${techDocId}&sectionId=${section.Id}`, { method: "POST", body: form })
            .then((r) => { if (!r.ok) throw new Error("upload"); return r.text(); })
            .then(() => reloadFiles())
            .catch(() => setError("Could not upload the file."))
            .finally(() => setIsUploading(false));
    }, [section?.Id, techDocId]);

    const reloadFiles = () => {
        fetch(`/umbraco/api/TechnicalDocumentation/Files/?techDocId=${techDocId}&sectionId=${section.Id}`)
            .then((r) => r.json())
            .then((data) => setFiles(Array.isArray(data) ? data : []))
            .catch(() => { /* noop */ });
    };

    const deleteFile = (fileId) => {
        fetch(`/umbraco/api/TechnicalDocumentation/Files/?techDocId=${techDocId}&sectionId=${section.Id}&fileId=${fileId}`, { method: "DELETE" })
            .then((r) => { if (!r.ok) throw new Error("del"); return r.text(); })
            .then(() => setFiles((prev) => prev.filter((f) => f.Id != fileId)))
            .catch(() => setError("Could not remove the file."));
    };

    const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: true });

    if (!section) return null;

    const svState = sectionVersion?.State ?? 0;
    const vLabel = sectionVersion ? `v${sectionVersion.Major}.${sectionVersion.Minor}` : "";

    // Source -> Target: Target depends on Source.
    const dependentsCount = (sectionLinks || []).filter((l) => l.SourceSectionIdentifier === section.SectionIdentifier).length;
    const dependenciesCount = (sectionLinks || []).filter((l) => l.TargetSectionIdentifier === section.SectionIdentifier).length;

    return (
        <>
        <div className={`td-editor-panel ${isEditing ? "editing" : ""}`}>
            <div className="td-section-head">
                <div className="td-row" style={{ gap: 10, marginBottom: 8 }}>
                    <span className="td-numbadge">{number}</span>
                    {isEditing ? (
                        <input
                            value={title}
                            onChange={(e) => { setTitle(e.target.value); onDirtyChange && onDirtyChange(true); }}
                            style={{
                                fontSize: 15, fontWeight: 700, color: "#1a202c", border: "none",
                                borderBottom: "1px dashed #cbd5e0", padding: "2px 0", flex: 1, minWidth: 0, background: "transparent",
                            }}
                        />
                    ) : (
                        <h3 style={{ fontSize: 15, color: "#1a202c", margin: 0, flex: 1 }}>{title}</h3>
                    )}
                    {vLabel && <span className="td-tag" title={sectionVersion?.Name || ""}>{vLabel}</span>}
                    <Shared.SectionVersionPill state={svState} />
                    {onOpenHistory && (
                        <span className="td-btn-outline" onClick={onOpenHistory} title="Version history">
                            <i className="fas fa-history" /> History
                        </span>
                    )}
                    {canEdit && !isEditing && (
                        <button className="td-btn td-btn-primary" onClick={startEdit}>
                            {svState === 1
                                ? <><i className="fas fa-code-branch" /> Create new draft</>
                                : <><i className="fas fa-pen" /> Edit</>}
                        </button>
                    )}
                </div>
            </div>

            {section.NeedsReview && (
                <div className="td-banner" style={{ margin: "10px 26px 0" }}>
                    <span><i className="fas fa-link" /> A section this one depends on was approved with changes &mdash; review whether this section still agrees with it.</span>
                    {canEdit && !isEditing && (
                        <span className="td-btn-outline" onClick={startEdit}>Create new draft</span>
                    )}
                    {canEdit && onClearNeedsReview && (
                        <span className="td-btn-outline" onClick={onClearNeedsReview}>Dismiss</span>
                    )}
                </div>
            )}

            {sectionVersion?.State === 2 && sectionVersion?.ReviewComment && (
                <div className="td-banner" style={{ margin: "10px 26px 0", background: "#fde8ec", borderBottom: "1px solid #f3b6c4" }}>
                    <div className="top" style={{ color: "#a3132f", fontSize: 14 }}>
                        <i className="fas fa-times-circle" /> Rejected — why this needs rework
                    </div>
                    <div className="note" style={{ color: "#7a0f24", fontSize: 14, lineHeight: 1.5 }}>{sectionVersion.ReviewComment}</div>
                </div>
            )}

            {isLoading ? (
                <div className="td-center-loading"><LoadingSpinnerImage /></div>
            ) : isEditing ? (
                <>
                    <div className="td-row" style={{ gap: 8, margin: "8px 26px 0" }}>
                        <span className="td-tspecial" style={{ margin: 0 }} onClick={() => setShowProductInfo(true)}>
                            <i className="fas fa-box-open" /> Insert from Product Info
                        </span>
                        <span className="td-tspecial muted" style={{ margin: 0 }} title="Available soon">
                            <i className="fas fa-robot" /> AI assistant
                        </span>
                    </div>
                    <div className="td-editor-body-wrap">
                        {!editorReady && <LoadingSpinnerImage />}
                        <div ref={editorElRef} />
                    </div>
                    {error && <div className="td-err" style={{ margin: "0 26px" }}>{error}</div>}
                    <div className="td-editor-actionbar">
                        <span className="meta">
                            {savedAt ? `Saved ${Shared.readableAge(savedAt)}`
                                : content?.UpdatedOn ? `Last saved ${Shared.readableAge(content.UpdatedOn)}` : ""}
                        </span>
                        <div className="right">
                            <button className="td-btn td-btn-cancel" onClick={cancel} disabled={isSaving}>Cancel</button>
                            <button className="td-btn td-btn-primary" onClick={save} disabled={isSaving}>
                                {isSaving ? "Saving…" : "Save section"}
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <Shared.SafeHTML className="td-readonly-body" html={content?.ContentText || "<p><em>No content yet.</em></p>"} />
                    {error && <div className="td-err" style={{ margin: "0 26px" }}>{error}</div>}
                </>
            )}

            {showProductInfo && (
                <InsertFromProductInfoPopup
                    techDocId={techDocId}
                    onInsert={(html) => { insertHtmlAtCursor(html); setShowProductInfo(false); }}
                    onClose={() => setShowProductInfo(false)}
                />
            )}
        </div>

        {/* overflow: visible while the kebab is open, so its dropdown (wider than this 270px panel's
            remaining space) isn't clipped by the panel's own scroll boundary -- see kebabOpen below. */}
        <div className="td-side-panel" style={kebabOpen ? { overflow: "visible" } : undefined}>
            <div className="td-panel-h" style={{ marginTop: 0 }}>
                Section info
                {canEdit && (svState === 0 || svState === 2) && (
                    <span className="td-kebab" ref={kebabRef} onClick={() => setKebabOpen(!kebabOpen)} style={{ float: "right", position: "relative" }}>
                        <i className="fas fa-ellipsis-v" />
                        {kebabOpen && (
                            <div className="td-kebab-menu" style={{ right: 0, top: 20, width: 220 }} onClick={(e) => e.stopPropagation()}>
                                <div className="td-kebab-menu-item" onClick={revertToApproved}>
                                    {reverting ? <LoadingSpinnerImage small /> : <><i className="fas fa-undo" /> Revert to latest approved version</>}
                                </div>
                            </div>
                        )}
                    </span>
                )}
            </div>
            <div className="td-kv"><span>State</span><span><Shared.SectionVersionPill state={svState} /></span></div>
            <div className="td-kv"><span>Version</span><span>{vLabel || "—"}{sectionVersion?.Name ? ` · ${sectionVersion.Name}` : ""}</span></div>
            {svState === 1 && sectionVersion?.PdfFileName && (
                <div className="td-kv">
                    <span>PDF</span>
                    <span>
                        <Shared.PdfDownloadLink
                            href={`/umbraco/api/TechnicalDocumentation/SectionVersionPdf/?techDocId=${techDocId}&sectionVersionId=${sectionVersion.Id}`}
                            filename={sectionVersion.PdfFileName}
                            label="Download"
                        />
                    </span>
                </div>
            )}
            <div className="td-kv"><span>Author</span><span>{Shared.userName(users, currentVersion?.Author) || "—"}</span></div>
            <div className="td-kv">
                <span>Reviewer{reviewers?.length > 1 ? "s" : ""}</span>
                <span>{reviewers?.length ? reviewers.map((r) => r.Name).filter(Boolean).join(", ") : "—"}</span>
            </div>
            <div className="td-kv"><span>Created</span><span>{Shared.formatDate(section.CreatedOn)}</span></div>
            <div className="td-kv"><span>Updated</span><span>{Shared.readableAge(section.UpdatedOn)}</span></div>

            <div className="td-panel-h">
                Jurisdictions
                {canEdit && !addingJurisdiction && (
                    <span className="td-tag td-tag-add" style={{ marginLeft: "auto" }} onClick={() => setAddingJurisdiction(true)}>+ Add jurisdiction</span>
                )}
            </div>
            {canEdit && addingJurisdiction && (
                <select
                    autoFocus defaultValue=""
                    onChange={(e) => addJurisdiction(e.target.value)}
                    onBlur={() => setAddingJurisdiction(false)}
                    style={{ width: "100%", fontSize: 11.5, borderRadius: 4, border: "1px solid #cbd5e0", padding: "4px 6px", marginBottom: 6 }}
                >
                    <option value="" disabled>Choose…</option>
                    {jurisdictionOptions
                        .filter((o) => !jurisdictions.find((j) => j.Id == o.Id))
                        .map((o) => <option key={o.Id} value={o.Id}>{o.Name}</option>)}
                </select>
            )}
            <div className="td-row" style={{ gap: 6, flexWrap: "wrap" }}>
                {jurisdictions.map((j) => (
                    <span key={j.Id} className="td-tag">
                        {j.Name}
                        {canEdit && (
                            <span className="x" onClick={() => removeJurisdiction(j.Id)}><i className="fas fa-times-circle" /></span>
                        )}
                    </span>
                ))}
                {jurisdictions.length === 0 && <span className="td-muted" style={{ fontSize: 11.5 }}>None</span>}
            </div>
            {canEdit && jurisdictionsChanged && (
                <div className="td-row" style={{ gap: 6, justifyContent: "flex-end", marginTop: 8 }}>
                    <span className="td-btn-outline" onClick={() => setJurisdictions(savedJurisdictions)}>Cancel</span>
                    <button className="td-btn td-btn-primary" style={{ padding: "4px 12px", fontSize: 12 }} onClick={saveJurisdictions} disabled={savingJurisdictions}>
                        {savingJurisdictions ? "Saving…" : "Save jurisdictions"}
                    </button>
                </div>
            )}

            <div className="td-hr" />
            <div className="td-panel-h">
                <i className="fas fa-paperclip" /> Attachments ({files.length})
                {canEdit && (
                    <span {...getRootProps()} className="td-tag td-tag-add" style={{ float: "right" }}>
                        <input {...getInputProps()} />
                        {isUploading ? "Uploading…" : "+ Add"}
                    </span>
                )}
            </div>
            <div className="td-attach-strip" style={{ margin: "0 0 4px" }}>
                {files.map((f) => (
                    <span key={f.Id} className="td-file-chip" style={{ display: "flex", marginBottom: 6 }}>
                        <i className="far fa-file" style={{ color: "#2b6cb0" }} />
                        {f.FileName}
                        {f.FileSize ? <span className="td-muted"> · {Shared.fileSize(f.FileSize)}</span> : null}
                        {canEdit && (
                            <span className="x" onClick={() => deleteFile(f.Id)}><i className="fas fa-times" /></span>
                        )}
                    </span>
                ))}
                {files.length === 0 && <span className="td-muted" style={{ fontSize: 11.5 }}>None</span>}
            </div>

            <div className="td-hr" />
            <div className="td-panel-h"><i className="fas fa-project-diagram" /> Dependencies</div>
            <span className="td-link" onClick={() => setDependencyPopup("see")} title="View dependencies">
                <i className="fas fa-sitemap" /> Depends on {dependenciesCount} · {dependentsCount} depend on it
            </span>
            {canEdit && (
                <div style={{ marginTop: 8 }}>
                    <span className="td-btn-outline" onClick={() => setDependencyPopup("add")}>
                        <i className="fas fa-plus" /> Add dependencies
                    </span>
                </div>
            )}

            <div className="td-hr" />
            <div className="td-panel-h"><i className="far fa-comment-alt" /> Comments</div>
            <span className="td-btn-outline" onClick={() => setShowComments(true)}>
                <i className="far fa-comments" /> View & add comments
            </span>
        </div>

        {showComments && (
            <div className="td-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setShowComments(false)}>
                <div className="td-modal wide">
                    <div className="td-modal-h">
                        <h3><i className="far fa-comment-alt" /> Comments · {number} {section.Title}</h3>
                        <span className="td-close-x" onClick={() => setShowComments(false)}><i className="fas fa-times" /></span>
                    </div>
                    <div className="td-modal-b">
                        <CommentsList
                            entityType={ENTITY_TYPE}
                            entityId={techDocId}
                            sectionId={section.Id}
                            userList={users}
                            currentUserId={currentUserId}
                            compact
                        />
                    </div>
                </div>
            </div>
        )}

        {dependencyPopup === "add" && (
            <AddDependenciesModal
                section={section} allSections={allSections || []} numbers={numbers} sectionLinks={sectionLinks || []}
                onAddLink={onAddLink} onRemoveLink={onRemoveLink} onClose={() => setDependencyPopup(null)}
            />
        )}
        {dependencyPopup === "see" && (
            <SeeDependenciesModal
                section={section} allSections={allSections || []} numbers={numbers} sectionLinks={sectionLinks || []}
                onClose={() => setDependencyPopup(null)}
            />
        )}
        </>
    );
});


export default SectionEditor;
